import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface Manzara {
    manzaraID: number;
    manzaraAdi: string;
    aktif: boolean;
}

interface ManzaraForm {
    manzaraAdi: string;
    aktif: boolean;
}

const Manzaralar: React.FC = () => {
    const [manzaralar, setManzaralar] = useState<Manzara[]>([]);
    const [selectedManzara, setSelectedManzara] = useState<Manzara | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<ManzaraForm>({ manzaraAdi: '', aktif: true });

    const fetchManzaralar = async () => {
        try {
            const response = await axios.get('/api/Manzaralar');
            setManzaralar(response.data);
        } catch {
            toast.error('Manzaralar yüklenirken bir hata oluştu');
        }
    };

    useEffect(() => { fetchManzaralar(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedManzara) {
                await axios.put(`/api/Manzaralar/${selectedManzara.manzaraID}`, formData);
                toast.success('Manzara güncellendi');
            } else {
                await axios.post('/api/Manzaralar', formData);
                toast.success('Manzara eklendi');
            }
            fetchManzaralar();
            handleCloseModal();
        } catch {
            toast.error('İşlem sırasında bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (manzara: Manzara) => {
        setSelectedManzara(manzara);
        setFormData({ manzaraAdi: manzara.manzaraAdi, aktif: manzara.aktif });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedManzara(null);
        setFormData({ manzaraAdi: '', aktif: true });
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .mn-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                /* ── Header ── */
                .mn-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .mn-header-left { display: flex; flex-direction: column; gap: 3px; }
                .mn-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .mn-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .mn-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .mn-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transform: translateY(-1px);
                }
                .mn-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Card ── */
                .mn-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                /* ── Table ── */
                .mn-table { width: 100%; border-collapse: collapse; }
                .mn-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .mn-table thead th {
                    padding: 13px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .mn-table thead th.mn-th-actions { text-align: right; }
                .mn-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .mn-table tbody tr:last-child { border-bottom: none; }
                .mn-table tbody tr.mn-row-hovered { background: #f0f5ff; }
                .mn-table tbody td {
                    padding: 15px 20px;
                    font-size: 14px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                /* Cell variants */
                .mn-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 56px; }
                .mn-cell-name { display: flex; align-items: center; gap: 12px; }
                .mn-view-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 9px;
                    background: #e0f2fe;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .mn-view-icon svg {
                    width: 17px; height: 17px;
                    stroke: #0369a1; stroke-width: 1.8;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }
                .mn-name-text { font-weight: 600; color: #0f172a; font-size: 14px; }
                .mn-cell-durum { width: 110px; }
                .mn-cell-actions { width: 90px; text-align: right; }

                /* Pill */
                .mn-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 11px 4px 8px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .mn-pill-aktif { background: #dcfce7; color: #15803d; }
                .mn-pill-pasif { background: #fee2e2; color: #b91c1c; }
                .mn-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                .mn-pill-aktif .mn-pill-dot { background: #22c55e; }
                .mn-pill-pasif .mn-pill-dot { background: #ef4444; }

                /* Ghost buttons */
                .mn-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .mn-row-hovered .mn-actions { opacity: 1; }
                .mn-ghost-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px; height: 34px;
                    border-radius: 8px;
                    border: 1.5px solid transparent;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .mn-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .mn-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .mn-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }

                /* Footer */
                .mn-footer {
                    padding: 13px 20px;
                    border-top: 1.5px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .mn-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .mn-count span { color: #475569; font-weight: 600; }

                /* Empty */
                .mn-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .mn-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .mn-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .mn
                .mn-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: mnDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                }
                @keyframes mnDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .mn-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .mn-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .mn-modal-close {
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: none; color: #9ca3af; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .mn-modal-close:hover { background: #f3f4f6; color: #374151; }
                .mn-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .mn-field label {
                    display: block;
                    font-size: 12px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em;
                    color: #64748b; margin-bottom: 6px;
                }
                .mn-field input[type="text"] {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                }
                .mn-field input:focus {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .mn-toggle-row {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 12px 14px;
                    background: #f8fafc; border-radius: 10px; border: 1.5px solid #e2e8f0;
                }
                .mn-toggle-label { font-size: 14px; font-weight: 500; color: #334155; }
                .mn-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
                .mn-switch input { opacity: 0; width: 0; height: 0; }
                .mn-switch-track {
                    position: absolute; inset: 0;
                    border-radius: 999px; background: #cbd5e1;
                    cursor: pointer; transition: background 0.2s;
                }
                .mn-switch input:checked + .mn-switch-track { background: #22c55e; }
                .mn-switch-track::after {
                    content: ''; position: absolute;
                    top: 3px; left: 3px; width: 18px; height: 18px;
                    border-radius: 50%; background: #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.18);
                    transition: transform 0.2s;
                }
                .mn-switch input:checked + .mn-switch-track::after { transform: translateX(18px); }
                .mn-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f3f4f6;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .mn-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .mn-btn-cancel:hover { background: #f9fafb; }
                .mn-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .mn-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .mn-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .mn-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: mnSpin 0.7s linear infinite;
                }
                @keyframes mnSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                {/* Header */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Manzaralar</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Daire manzaralarını yönetin</p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Manzara
                    </button>
                </div>

                {/* Card List */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                    {manzaralar.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>🏔️</div>
                            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Henüz kayıt eklenmedi</p>
                        </div>
                    ) : (
                        manzaralar.map((manzara, idx) => (
                            <div
                                key={manzara.manzaraID}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: idx < manzaralar.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.13s' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <div>
                                    <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{manzara.manzaraAdi}</p>
                                    <p style={{ fontSize: 12, margin: '2px 0 0', color: manzara.aktif ? '#059669' : '#e11d48', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: manzara.aktif ? '#10b981' : '#f43f5e', display: 'inline-block' }} />
                                        {manzara.aktif ? 'Aktif' : 'Pasif'}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button title="Düzenle" onClick={() => handleEdit(manzara)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#2563eb', display: 'flex', transition: 'background 0.13s' }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                    >
                                        <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </button>
                                    <button title="Sil" onClick={() => toast.info('Silme için onay gerekli')}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#f43f5e', display: 'flex', transition: 'background 0.13s' }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#fff1f2')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                    >
                                        <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="mn-overlay" onClick={handleCloseModal}>
                    <div className="mn-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedManzara ? 'Manzara Düzenle' : 'Yeni Manzara Ekle'}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mn-modal-body">
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Manzara Adı</label>
                                    <input
                                        type="text"
                                        id="manzaraAdiInput"
                                        placeholder="Doğa, Protokol Yolu, Deniz..."
                                        value={formData.manzaraAdi}
                                        onChange={e => setFormData({ ...formData, manzaraAdi: e.target.value })}
                                        required
                                        maxLength={50}
                                        autoFocus
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div className="mn-toggle-row">
                                    <span className="mn-toggle-label">Durum — Aktif</span>
                                    <label className="mn-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.aktif}
                                            onChange={e => setFormData({ ...formData, aktif: e.target.checked })}
                                        />
                                        <span className="mn-switch-track" />
                                    </label>
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >İptal</button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', opacity: saving ? 0.75 : 1 }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                >
                                    {saving && <span className="mn-spinner" />}
                                    {selectedManzara ? 'Güncelle' : 'Ekle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Manzaralar;